import { ElementRef, Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ProductImage } from '@/components/ProductImage';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/Carousel';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useGetSalesByUserProfileId } from '@/hooks/useGetSalesByUserProfileId';
import { cn, priceFormatterV2 } from '@/lib/utils';
import { Crm } from '@/services/CrmService';

interface PurchaseHistoryProps {
  crm: Pick<Crm, 'userProfileId' | 'catalogImages'>;
}

export function PurchaseHistory({ crm }: PurchaseHistoryProps) {
  const [viewOldHistory, setViewOldHistory] = useState(false);

  const endSalesRef = useRef<ElementRef<'div'>>(null);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Profiles.components.ProfileModal',
  });

  const isCarouselActive = crm.catalogImages.length > 3;

  const { user } = useAuth();

  const { sales } = useGetSalesByUserProfileId(crm?.userProfileId ?? '');

  const formattedSales = viewOldHistory ? sales : sales.slice(0, 3);

  function handleOldHistory() {
    setViewOldHistory(true);
  }

  return (
    <div className="space-y-2">
      <div className="px-5 py-1">
        <h4 className="text-sm font-semibold">{t('saleHistory')}</h4>
      </div>

      <Carousel
        opts={{
          align: 'start',
          active: isCarouselActive,
        }}
        className="w-full"
      >
        <CarouselContent className="w-full">
          {crm.catalogImages.map((cimg) => (
            <CarouselItem key={cimg.catalogId} className="lg:basis-1/3">
              <Link
                to={ROUTE_PATHS.VISION_PRODUCT.replace(
                  ':productId',
                  cimg.catalogId.toString(),
                )}
                target="_blank"
              >
                <ProductImage
                  imageProps={{ src: cimg.imageUrl }}
                  fallbackProps={{ showMessage: true }}
                />
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>

        {isCarouselActive && (
          <>
            <CarouselPrevious className="left-0" />
            <CarouselNext className="right-0" />
          </>
        )}
      </Carousel>

      <div className="space-y-8 px-5">
        <div
          className={cn(
            'p-5',
            viewOldHistory
              ? 'max-h-[290px] overflow-y-scroll'
              : 'max-h-[260px] overflow-hidden',
          )}
        >
          <ul>
            {formattedSales.map((sale, index) => (
              <Fragment key={sale.id}>
                <li className="group flex gap-[10px]">
                  <div className="flex flex-col items-center">
                    <span className="block w-[2px] flex-1 bg-gradient-to-t from-gray-300 to-gray-300 group-first:to-transparent" />
                    <span className="block h-4 w-4 rounded-full bg-green-500" />
                    <span className="bottom-line block w-[2px] flex-1 bg-gradient-to-b from-gray-300 to-gray-300 group-last:to-transparent" />
                  </div>

                  <div className="space-y-1">
                    <span className="block text-xs text-muted-foreground">
                      {t('purchaseMadeAt', {
                        date: sale?.creationDate
                          ? new Date(sale.creationDate).toLocaleDateString(
                              user!.customer.language,
                            )
                          : '?',
                      })}
                    </span>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="block max-w-[400px] truncate text-sm">
                            {sale.products.join(' + ')}
                          </button>
                        </TooltipTrigger>

                        <TooltipContent className="max-w-[400px] p-4">
                          <span className="text-sm">
                            {sale.products.join(', ')}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <div className="flex items-center gap-[10px]">
                      <span className="block text-xs text-muted-foreground">
                        {t('amount', {
                          value: priceFormatterV2(
                            sale.value,
                            user!.customer.language,
                            user!.customer.platform,
                          ),
                        })}
                      </span>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button className="block max-w-[260px] truncate text-xs text-muted-foreground">
                              {t('categories')}: {sale.categories.join(', ')}
                            </button>
                          </TooltipTrigger>

                          <TooltipContent className="max-w-[400px] p-4">
                            <span className="text-sm">
                              {sale.categories.join(', ')}
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </li>

                {index + 1 < sales.length && (
                  <li className="ml-[7px] h-5 w-[2px] bg-gray-300 py-2" />
                )}
              </Fragment>
            ))}
          </ul>

          <div ref={endSalesRef} />
        </div>

        {!viewOldHistory && sales.length > 3 && (
          <button
            className="text-xs text-blue-500 underline"
            onClick={handleOldHistory}
          >
            {t('viewOldHistory')}
          </button>
        )}
      </div>
    </div>
  );
}
